<script>
import LegacyFilePicker from "../upload/FilePicker.vue";
import {
  isFallbackPickerError,
  isFileSystemAccessSupported,
  isPickerCancel,
  pickDirectoryWithFileSystemAccess,
  pickFilesWithFileSystemAccess,
} from "../../../util/uploadFileSystemAccess";
import { uploadDiagnosticLog } from "../../../util/uploadDiagnostics";

function fallbackToInputPicker(component, refName = "fileUpload") {
  uploadDiagnosticLog("info", "picker-fallback-legacy-input", {
    pickerPath: component?.$options?.name || "legacy-input",
  });
  component.isDisabled = false;
  component.$refs[refName].click();
}

export default {
  name: "UploadV2Picker",
  extends: LegacyFilePicker,
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isPickerDisabled() {
      return this.disabled || this.isDisabled;
    },
    handleDrop(event) {
      if (this.disabled) {
        return;
      }
      return LegacyFilePicker.methods.handleDrop.call(this, event);
    },
    handleFileInput(event) {
      if (this.disabled) {
        return;
      }
      return LegacyFilePicker.methods.handleFileInput.call(this, event);
    },
    handleFolderInput(event) {
      if (this.disabled) {
        return;
      }
      this.handleFileInput(event);
    },
    async triggerFileInput() {
      if (this.disabled) {
        return;
      }
      if (!isFileSystemAccessSupported()) {
        uploadDiagnosticLog("info", "picker-path-selected", { pickerPath: "legacy-file-input", reason: "unsupported" });
        fallbackToInputPicker(this);
        return;
      }
      if (this.disabled || this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      try {
        const files = await pickFilesWithFileSystemAccess(window, {
          onSelectionAccepted: () => this.$emit("selectionProcessingStarted"),
        });
        if (!files.length) {
          this.isDisabled = false;
        }
        this.$emit("filesReady", files);
      } catch (error) {
        if (isFallbackPickerError(error)) {
          uploadDiagnosticLog("warn", "picker-file-system-access-fallback", { pickerPath: "legacy-file-input" }, error);
          fallbackToInputPicker(this);
          return;
        }
        this.isDisabled = false;
        if (!isPickerCancel(error)) {
          uploadDiagnosticLog("warn", "picker-selection-failed", { pickerPath: "file-system-access-files" }, error);
          console.error(error);
        } else {
          uploadDiagnosticLog("info", "picker-cancelled", { pickerPath: "file-system-access-files" });
        }
      }
    },
    async triggerFolderInput() {
      if (this.disabled) {
        return;
      }
      if (!isFileSystemAccessSupported()) {
        uploadDiagnosticLog("info", "picker-path-selected", { pickerPath: "legacy-folder-input", reason: "unsupported" });
        fallbackToInputPicker(this, "folderUpload");
        return;
      }
      if (this.disabled || this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      try {
        const files = await pickDirectoryWithFileSystemAccess(window, {
          onSelectionAccepted: () => this.$emit("selectionProcessingStarted"),
        });
        if (!files.length) {
          this.isDisabled = false;
        }
        this.$emit("filesReady", files);
      } catch (error) {
        if (isFallbackPickerError(error)) {
          uploadDiagnosticLog("warn", "picker-file-system-access-fallback", { pickerPath: "legacy-folder-input" }, error);
          fallbackToInputPicker(this, "folderUpload");
          return;
        }
        this.isDisabled = false;
        if (!isPickerCancel(error)) {
          uploadDiagnosticLog("warn", "picker-selection-failed", { pickerPath: "file-system-access-directory" }, error);
          console.error(error);
        } else {
          uploadDiagnosticLog("info", "picker-cancelled", { pickerPath: "file-system-access-directory" });
        }
      }
    },
  },
};
</script>

<template>
  <section
    class="upload-v2-picker"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
    :class="{ active: dragOver > 0, disabled: isPickerDisabled() }"
  >
    <div class="upload-v2-picker-copy">
      <span class="upload-v2-picker-title">{{ $t("upload-v2-picker-title") }}</span>
      <span class="upload-v2-picker-description">{{ $t("upload-v2-picker-description") }}</span>
    </div>
    <div class="upload-v2-picker-actions">
      <button
        type="button"
        class="primary"
        @click.prevent="triggerFileInput"
        :disabled="isPickerDisabled()"
      >
        {{ $t("browse-files") }}
      </button>
      <button
        type="button"
        @click.prevent="triggerFolderInput"
        :disabled="isPickerDisabled()"
      >
        {{ $t("browse-folders") }}
      </button>
    </div>
    <input
      ref="fileUpload"
      multiple
      name="file-upload"
      type="file"
      @change="handleFileInput"
      :disabled="isPickerDisabled()"
    />
    <input
      ref="folderUpload"
      multiple
      name="folder-upload"
      type="file"
      webkitdirectory
      @change="handleFolderInput"
      :disabled="isPickerDisabled()"
    />
  </section>
</template>

<style lang="scss" scoped>
.upload-v2-picker {
  background: #fff;
  border: 1px solid #C8D2DC;
  border-radius: 8px;
  box-sizing: border-box;
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  padding: 2rem;
  transition: border-color 120ms ease, box-shadow 120ms ease;

  &.active {
    border-color: #1275CC;
    box-shadow: 0 0 0 3px rgba(18, 117, 204, 0.16);
  }

  &.disabled {
    opacity: 0.72;
  }

  input[type=file] {
    display: none;
  }
}

.upload-v2-picker-copy {
  display: grid;
  gap: 0.5rem;
}

.upload-v2-picker-title {
  color: #1F2933;
  font-weight: 700;
}

.upload-v2-picker-description {
  color: #4B5563;
  font-size: 0.875rem;
  line-height: 1.4;
}

.upload-v2-picker-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;

  button {
    background: transparent;
    border: 1px solid #1275CC;
    color: #1275CC;
    border-radius: 4px;
    padding: 0.75rem 1rem;
    font-weight: bold;
    cursor: pointer;
    line-height: 1.25rem;
    min-width: 8rem;

    &:disabled {
      cursor: default;
      opacity: 0.4;
    }

    &.primary {
      background: #1275CC;
      border-color: transparent;
      color: white;
    }
  }
}

@media (max-width: 720px) {
  .upload-v2-picker {
    grid-template-columns: 1fr;
  }

  .upload-v2-picker-actions {
    justify-content: flex-start;

    button {
      flex: 1 1 10rem;
    }
  }
}
</style>
